import { faCheck } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { Button } from '../../flayr-components/Button/Button';
import { Flex } from '../../flayr-components/Flex/Flex';
import { Img } from '../../flayr-components/Img/Img';
import CheckCircle from '../../lib/CheckCircle/CheckCircle';
import Btn from '../../reskin-lib/Btn';
import { IAppointment } from '../../types/appointment';
import { IPricing } from '../../types/pricing';
import { getMailToLink } from '../../utils';
import PriceAmount from '../PriceAmount/PriceAmount';

interface IProfileFooterProps {
  pricing?: IPricing;
  provider?: any;
  appointment?: IAppointment;
  currency?: string;
  className?: string;
  status?: string;
  placeholder?: boolean;
  hideBtns?: string[];
  onCtaClick?: (e?: any) => void;
  onLearnMore?: (e?: any) => void;
  onReschedule?: (e?: any) => void;
  onChange?: (e?: any) => void;
  onCancelChangeRequest?: (e: any) => void;
  onCancel?: (e?: any) => void;
}

const ProfileFooter: React.FC<React.PropsWithChildren<React.PropsWithChildren<IProfileFooterProps>>> = ({
  provider,
  appointment,
  onCtaClick,
  placeholder,
  onLearnMore,
  // pricing,
  // currency,
  className,
  onCancel,
  onReschedule,
  onChange,
  onCancelChangeRequest,
  hideBtns = [],
}) => {
  const CtaBtn = ({ children }) => {
    return <Btn onClick={onCtaClick}>{children}</Btn>;
  };

  function getLeftContent() {
    if (!appointment || (appointment.status as any) === 'open') {
      return (
        <Flex>
          <div className="leading-tight text-gray-700 text-[13px]">
            {/* No Credit Card Required <br /> */}
            <FontAwesomeIcon icon={faCheck} className="mr-2" />
            No obligation
          </div>
        </Flex>
      );
    }

    switch (appointment.status) {
      case 'requested':
        return (
          <div className="text-sm leading-tight">
            <p>Request more providers</p>
            <p>Its free</p>
          </div>
        );
      case 'changePackageRequested':
      case 'changePackageOffered':
        return (
          <Btn
            onClick={onCancelChangeRequest}
            clear
            color="danger"
            className="-ml-3"
          >
            Cancel Request
          </Btn>
        );
      case 'offered':
      case 'timeChangeRequested':
        return (
          <>
            {/* <div className="text-sm leading-none">
              <span>Deposit now</span> <br />
              <span className="text-gray-600">rest on the day</span>
            </div> */}
            <div
              className="text-sm leading-none xs:text-xs"
              onClick={onLearnMore}
            >
              <span>COVID Cancellation</span>
              <br />
              <span className="text-sm underline cursor-pointer text-primary xs:text-xs">
                Learn more
              </span>
            </div>
          </>
        );
      case 'offerExpired':
        return (
          <div className="text-xs capitalize text-gray-5E">Offer expired</div>
        );
      case 'expired':
      case 'declined':
      case 'bookedOut':
      case 'deleted':
        return <div className="text-sm"></div>;
      case 'booked':
        return (
          <Btn color="danger" clear className="-ml-3" onClick={onCancel}>
            Cancel booking
          </Btn>
          // <a
          //   href={getMailToLink(`Cancel booking - ${appointment.id}`)}
          //   className="block"
          //   target="__blank"
          // >
          // </a>
        );
      case 'cancelled':
      case 'cancelledByCustomer':
      case 'cancelledByProvider':
      case 'cancelledByFlayr':
        return <span className="text-red-500">Booking Cancelled</span>;
      case 'closed':
        return <span className="text-red-500">Request Closed</span>;
      case 'rescheduleOffered':
        return (
          <Btn clear className="-ml-3 font-medium" onClick={onReschedule}>
            Other
          </Btn>
        );
      default:
        return null;
    }
  }

  function getRightContent() {
    if (!appointment || (appointment.status as any) === 'open') {
      return <CtaBtn>Check Availability</CtaBtn>;
    }
    switch (appointment.status) {
      case 'requested':
        return (
          <div className="inline-flex items-center text-secondary-1">
            <div className="mr-2 text-2xl">
              <CheckCircle />
            </div>
            <p className="leading-tight">
              Checking <br />
              Availability
            </p>
          </div>
        );

      case 'offerExpired':
        return <CtaBtn children="Request Again" />;
      case 'timeChangeRequested':
      case 'offered':
        return (
          <CtaBtn
            children={
              <>
                Book Now -{' '}
                <PriceAmount
                  currency={appointment?.currencySymbol}
                  deposit={appointment?.deposit}
                  amount={appointment.amount}
                  className="ml-1"
                />
              </>
            }
          />
        );

      case 'booked':
      case 'cancelled':
      case 'cancelledByCustomer':
      case 'cancelledByProvider':
      case 'cancelledByFlayr':
      case 'closed':
      case 'changePackageRequested':
        return (
          <CtaBtn children={<span className="text-base">Contact Flayr</span>} />
        );
      case 'rescheduleOffered':
        return <CtaBtn children="ACCEPT NEW SPOT" />;
      case 'changePackageOffered':
        return <CtaBtn children="View Offer" />;
      case 'expired':
      case 'declined':
      case 'bookedOut':
      case 'dismissed':
      case 'deleted':
        return <div className="text-red-500">Booked Out</div>;
      default:
        return null;
    }
  }

  return (
    (!placeholder && (
      <div
        className={`fixed bottom-0 left-0 z-10 w-full ${className || ''}`}
        data-testid="profile-footer"
      >
        <Flex
          className="p-5 bg-white"
          style={{ boxShadow: '0px 0px 35px rgba(0, 0, 0, 0.1)' }}
        >
          {!['booked', 'rescheduleRequested'].includes(
            appointment?.status || ''
          ) && (
            <>
              <div className="pl-3">{getLeftContent()}</div>
              <div className="px-2 xs:px-0"></div>
              <div className="ml-auto">{getRightContent()}</div>
            </>
          )}
          {['booked', 'rescheduleRequested'].includes(
            appointment?.status || ''
          ) && (
            <>
              <div className="flex-grow">
                <Btn
                  color="danger"
                  clear
                  className="-ml-3"
                  size="sm"
                  onClick={onCancel}
                  hidden={hideBtns.includes('cancel')}
                >
                  Cancel
                </Btn>
              </div>
              {/* <div className="flex-grow text-center">
                <Button
                  hidden={hideBtns.includes('reschedule')}
                  clear
                  size="sm"
                  onClick={onReschedule}
                >
                  Reschedule
                </Button>
              </div> */}
              <div className="flex-grow text-center">
                <Btn
                  hidden={hideBtns.includes('change')}
                  clear
                  size="sm"
                  onClick={onChange}
                >
                  Change
                </Btn>
              </div>
              <div className="flex-grow text-right">
                <Btn onClick={onCtaClick} size="sm">
                  HELP
                </Btn>
              </div>
            </>
          )}
        </Flex>
      </div>
    )) ||
    null
  );
};
export default ProfileFooter;
